import { ArrowUp, FileText as CommandIcon, Paperclip, Square, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Textarea } from "@/components/atomic/Textarea";
import { useAgentRunMutations } from "@/hooks/agent/useAgentRunMutations";
import { useAgentRuns } from "@/hooks/agent/useAgentRuns";
import { useFileUpload } from "@/hooks/agent/useFileUpload";
import { useSendMessage } from "@/hooks/agent/useSendMessage";
import { useThreadCosts } from "@/hooks/agent/useThreadCosts";
import { Command } from "@/models/agent/command";
import { useAgentStore } from "@/stores/agent/useAgentStore";
import { Button } from "./Button";
import { CommandSelector } from "./CommandSelector";

interface AgentChatInputProps {
  placeholder?: string;
  className?: string;
}

const AgentChatInput: React.FC<AgentChatInputProps> = ({
  placeholder = "Ask anything",
  className = "",
}) => {
  const { threadId } = useParams();
  const [messageInput, setMessageInput] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const [selectedCommand, setSelectedCommand] = useState<Command | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentAgentRunId = useAgentStore((state) => state.currentAgentRunId);
  const setCurrentAgentRunId = useAgentStore((state) => state.setCurrentAgentRunId);
  const { handleSendMessage, isSending } = useSendMessage();
  const { selectedFiles, setSelectedFiles, uploadAllFiles, isUploading } = useFileUpload();

  const isNewConversation = !threadId;

  // hooksを使用してデータ取得
  const { threadCost } = useThreadCosts(threadId || null);
  const { agentRuns } = useAgentRuns(threadId || null);
  const { cancelAgent } = useAgentRunMutations();

  // スレッド表示時に実行中のagent_runを取得
  useEffect(() => {
    const runningAgentRun = agentRuns.find((run) => run.isRunning);
    setCurrentAgentRunId(runningAgentRun ? runningAgentRun.id : null);
  }, [agentRuns, setCurrentAgentRunId]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (!files) return;

    setSelectedFiles(prev => [...prev, ...files]);
    event.target.value = "";
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async () => {
    if (!canSubmit) return;

    try {
      if (threadId) {
        const filePaths = await uploadAllFiles(`/workspace/${threadId}/data`);

        const trimmed = messageInput.trim();
        if (trimmed) {
          const fileText = filePaths.map(path => `[UploadedFile: ${path}]`).join("\n");
          const finalMessage = fileText
            ? `${messageInput.trim()}\n\n${fileText}`
            : messageInput.trim();
          await handleSendMessage(finalMessage, selectedCommand?.name);
        } else {
          toast.success("ファイルをアップロードしました。");
        }
      } else {
        await handleSendMessage(messageInput.trim(), selectedCommand?.name);
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      return;
    }

    // 状態をクリア
    setMessageInput("");
    setSelectedFiles([]);
    setSelectedCommand(null);
  };

  const onCancel = async () => {
    if (!currentAgentRunId) return;

    await cancelAgent(currentAgentRunId);
    setCurrentAgentRunId(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Shift+Enter または Cmd+Enter で送信、Enter単体は改行
    if (e.key === "Enter" && (e.shiftKey || e.metaKey) && !isComposing) {
      e.preventDefault();
      onSubmit();
    }
  };

  const isAgentRunning = !!currentAgentRunId;

  const canSubmit = threadId
    ? (messageInput.trim() || selectedFiles.length) && !isAgentRunning && !isUploading && !isSending
    : messageInput.trim() && !isAgentRunning && !isUploading && !isSending;

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = () => {
    setIsComposing(false);
  };

  return (
    <div className={`space-y-4 relative ${className}`}>
      {/* Selected Files */}
      {selectedFiles.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedFiles.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center gap-2 bg-gray-100 rounded-md px-3 py-1 text-sm"
            >
              <Paperclip className="h-3 w-3" />
              <span className="truncate max-w-[120px]">{file.name}</span>
              <button
                onClick={() => removeFile(index)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Text Input with Icons */}
      <div className="relative bg-white rounded-[20px] border border-gray-200 p-2.5 pt-4 max-h-[250px] flex flex-col mb-1">
        {/* Input Field */}
        <div className="flex-1 min-h-0 overflow-y-auto mb-3 px-2">
          <Textarea
            placeholder={placeholder}
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            className="w-full border-none shadow-none bg-transparent p-0 text-base placeholder:text-gray-400 focus-visible:ring-0 resize-none min-h-[24px] markdown"
            rows={1}
          />
        </div>

        {/* Buttons Row */}
        <div className="flex items-center justify-between flex-shrink-0">
          {/* Left Side Buttons */}
          <div className="flex items-center gap-2">
            {/* Attach Button */}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600 cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading || isAgentRunning || !threadId}
            >
              <Paperclip className="h-4 w-4" />
            </Button>

            {/* Command Selector */}
            {!selectedCommand && (
              <CommandSelector
                onSelect={setSelectedCommand}
                disabled={isUploading || isAgentRunning}
              />
            )}

            {/* Selected Command Badge */}
            {selectedCommand && (
              <div className="flex items-center gap-1.5 h-7 bg-blue-100 rounded-md px-2 text-sm">
                <CommandIcon className="h-3.5 w-3.5 text-blue-500" />
                <span className="text-blue-500 font-medium">{selectedCommand.name}</span>
                <button
                  onClick={() => setSelectedCommand(null)}
                  className="text-blue-500 hover:text-blue-400 cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>

          {/* Right Side Icons */}
          <div className="flex items-center gap-2">
            {isAgentRunning
              ? (
                <Button
                  size="sm"
                  className="h-8 w-8 p-0 rounded-full bg-[#6888E1] hover:bg-[#6888E1]/90 cursor-pointer"
                  onClick={onCancel}
                >
                  <Square className="scale-84 fill-[#ffffff]" />
                </Button>
              )
              : (
                <Button
                  size="sm"
                  className="h-8 w-8 p-0 rounded-full bg-[#6888E1] hover:bg-[#6888E1]/90 cursor-pointer"
                  onClick={onSubmit}
                  disabled={!canSubmit}
                >
                  <ArrowUp
                    className="h-4 w-4"
                    strokeWidth={3}
                  />
                </Button>
              )}
          </div>
        </div>
      </div>

      {/* コスト表示 - 新規会話でない場合のみ表示 */}
      {!isNewConversation && (
        <div className="absolute right-1 bottom-[-33px] text-xs text-gray-400">
          Cost : ${threadCost.getTotalCost().toFixed(4)}
        </div>
      )}

      {/* 非表示のファイル入力 */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        style={{ display: "none" }}
        accept="*/*"
        multiple
      />
    </div>
  );
};

export default AgentChatInput;
